import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";

export interface Product {
  id: string;
  attributes: {
    name: string;
    description: string;
    price: number;
    price_formatted: string;
    buy_now_url: string;
  };
}

const getProductsFn = httpsCallable<void, { data: Product[] }>(
  functions,
  "getProducts",
);

// Cache the in-flight request at module scope so multiple consumers
// (the promo bar + the pricing section) share a single network call.
let productsPromise: Promise<Product[]> | null = null;
export const fetchProducts = () => {
  if (!productsPromise) {
    productsPromise = getProductsFn()
      .then((result) => result.data.data)
      .catch((err) => {
        // Reset so a later mount can retry instead of caching the failure.
        productsPromise = null;
        throw err;
      });
  }
  return productsPromise;
};

export interface UseProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  monthlyProduct?: Product;
  yearlyProduct?: Product;
  savingsPercentage: number;
}

export const useProducts = (): UseProductsResult => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchProducts()
      .then((data) => {
        if (!cancelled) setProducts(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "An error occurred");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const monthlyProduct = products.find(
    (p) =>
      p.attributes.name.toLowerCase().includes("monthly") ||
      (!p.attributes.name.toLowerCase().includes("yearly") &&
        p === products[0]),
  );

  // Prefer the "Yearly Discount" plan over the plain "Yearly" one when present,
  // so the discounted price (and its larger savings %) is what gets shown.
  const yearlyProduct =
    products.find((p) =>
      p.attributes.name.toLowerCase().includes("yearly discount"),
    ) ??
    products.find(
      (p) =>
        p.attributes.name.toLowerCase().includes("yearly") ||
        (!p.attributes.name.toLowerCase().includes("monthly") &&
          p === products[1]),
    );

  let savingsPercentage = 0;
  if (monthlyProduct && yearlyProduct) {
    const monthlyPrice = monthlyProduct.attributes.price / 100;
    const yearlyPrice = yearlyProduct.attributes.price / 100;
    const monthlyEquivalent = yearlyPrice / 12;
    savingsPercentage = Math.round(
      (1 - monthlyEquivalent / monthlyPrice) * 100,
    );
  }

  return {
    products,
    loading,
    error,
    monthlyProduct,
    yearlyProduct,
    savingsPercentage,
  };
};
